import React, { ReactNode, useEffect, useState } from 'react';
import { Button, Form, Stack } from 'react-bootstrap';
import ReactSelect from 'react-select';
import { Range, getTrackBackground } from 'react-range';
import { useFormik } from 'formik';
import { PreferencesInterface } from 'constants/interfaces';
import useTranslate from 'hooks/useTranslate';
import { preferencesInitialValues, preferencesSchema } from 'constants/schemaValidations';
import {
	useGetPreferencesFieldsQuery,
	useGetPreferencesQuery,
	useJobListFilterQuery,
	useSavePreferencesMutation,
} from 'services';
import { CustomMultiSearchAddress } from 'components/Common/CustomMultiSearchAddress';
import CreatableSelect from 'react-select/creatable';
import toast from 'react-hot-toast';
import { getArrayKeyWise, getSalaryRange, removeLocationsFromDropdown, unitNumber } from 'utils';
import './styles.scss';

const JobPreference: React.FC = () => {
	const [priceRange, setPriceRange] = useState([1]);
	const STEP = 1;

	const [min, setMin] = useState(1);
	const [max, setMax] = useState(100);

	const { translate } = useTranslate();

	const { data: commonFields = [] } = useGetPreferencesFieldsQuery();
	const { data: { data: savedPreferences = [] } = {}, refetch } = useGetPreferencesQuery();

	const { data: { salary: salaryArr = [] } = {} } = useJobListFilterQuery();

	const {
		locations = [],
		language = [],
		jobRole = [],
		level = [],
		isCompanySizeSpecific = 'no',
		companySize = '',
		isIndustoriesFavourite = 'no',
		industries: savedIndustries = [],
		salary: savedSalary = 0,
		skills = [],
		salaryType = '',
	} = savedPreferences;
	const salary = parseInt(savedSalary);

	const savedDataArr = {
		countryIds: getArrayKeyWise(locations, 'countryId'),
		stateIds: getArrayKeyWise(locations, 'stateId'),
		cityIds: getArrayKeyWise(locations, 'cityId'),
		locations: locations,
		language: language,
		newRole: [],
		jobRole: jobRole,
		level: getArrayKeyWise(level, 'id'),
		isCompanySizeSpecific: isCompanySizeSpecific,
		companySize: companySize,
		isIndustoriesFavourite: isIndustoriesFavourite,
		industries: getArrayKeyWise(savedIndustries, 'id'),
		salary: salary,
		skills: skills,
		salaryType: salaryType,
	};

	useEffect(() => {
		refetch();
	}, []);

	useEffect(() => {
		if (salary > 0) {
			setPriceRange([salary]);
		}
	}, [salary]);

	useEffect(() => {
		// Get new salary range data
		const salaryData = getSalaryRange(salaryArr, savedPreferences.salaryType);
		// Only update if values actually change
		if (salaryData) {
			if (min !== salaryData.minSalary) setMin(salaryData.minSalary);
			if (max !== salaryData.maxSalary) setMax(salaryData.maxSalary);
			if (priceRange[0] !== salaryData.minSalary) setPriceRange([salaryData.minSalary]);
		}
		// Also, if salary changes, update priceRange
		if (salary > 0 && priceRange[0] !== salary) {
			setPriceRange([salary]);
		}
	}, [savedPreferences.salaryType, salaryArr, salary]);

	const callSalaryRangeFunc = (salaryArr: any, salaryType: any) => {
		const salaryData: any = getSalaryRange(salaryArr, salaryType);
		setPriceRange([salaryData?.minSalary]);
		setMax(salaryData?.maxSalary);
		setMin(salaryData?.minSalary);
	};

	const {
		companyRangeList = [],
		industries = [],
		languages = [],
		levels = [],
		roles = [],
		skill = [],
	} = commonFields;

	// mutation
	const { mutateAsync: savePreferencesMutation, isPending } = useSavePreferencesMutation();

	const formik: any = useFormik({
		initialValues: { ...preferencesInitialValues, ...savedDataArr },
		validationSchema: preferencesSchema(translate),
		enableReinitialize: true,
		onSubmit: async (values) => {
			const payload = {
				...values,
				skills: values.skills.map((item: any) => item.id),
			};
			savePreferencesMutation({ ...payload }).then((res: any) => {
				if (res?.success) {
					toast.success(res?.message);
				}
			});
		},
	});
	const { handleChange, values } = formik;
	const renderError = <T extends keyof PreferencesInterface>(field: T) =>
		formik.touched[field] && formik.errors[field] ? (
			<span className="text-danger f-error">{formik.errors[field] as ReactNode}</span>
		) : null;

	const getNameById = (arr: any, id: any, key: any) => {
		if (arr?.length) {
			return arr.find((item: any) => item?.id === Number(id))?.[key];
		}
	};

	const handleCheckboxChange = (event: any) => {
		const { checked, value, name }: any = event.target;
		const newLevel = parseInt(value, 10);
		const formikValues = formik.values;
		if (formikValues) {
			const currentValue = formikValues[name];
			formik.setFieldValue(
				name,
				checked ? [...currentValue, newLevel] : currentValue?.filter((id: any) => id !== newLevel),
			);
		}
	};

	const renderIssue = () => {
		if (formik.values.countryIds?.length > 2) {
			return <span className="text-danger f-error">{translate('Form.countryLimit')} </span>;
		} else if (formik.values.stateIds?.length > 7) {
			return <span className="text-danger f-error">{translate('Form.stateLimit')} </span>;
		} else if (formik.values.cityIds?.length > 15) {
			return <span className="text-danger f-error">{translate('Form.cityLimit')} </span>;
		}
	};

	return (
		<Form onSubmit={formik.handleSubmit}>
			<Stack className="job-preference-inner">
				<div className="job-preference-box bg-white">
					<Stack className="job-preference-form">
						<div>
							<Form.Group className="position-relative search-select">
								<Form.Label className="mb-2 mb-md-3 mb-xxl-4">
									{translate('Pages.setYourJobPreference')}
								</Form.Label>
								<CustomMultiSearchAddress formik={formik} />
								<i className="ri-search-line form-icon position-absolute lh-1"></i>
								{renderIssue()}
								{renderError('locations')}
							</Form.Group>
							{formik.values.locations?.length > 0 && (
								<div className="selected-items-list d-flex flex-wrap gap-2 gap-md-3 mt-3 mt-md-4">
									{formik.values.locations.map((item: any, index: number) => {
										return (
											<div
												key={index}
												className="selected-item-box d-flex align-items-center gap-2 rounded-pill"
											>
												<p className="mb-0">{item?.location}</p>
												<Button
													variant="link"
													className="p-0 text-decoration-none border-0"
													onClick={() => removeLocationsFromDropdown(formik, item)}
												>
													<i className="ri-close-line"></i>
												</Button>
											</div>
										);
									})}
								</div>
							)}
						</div>
					</Stack>
				</div>
				<div className="job-preference-box bg-white">
					<Stack className="job-preference-form">
						<Form.Group className="position-relative">
							<Form.Label className="mb-2 mb-md-3 mb-xxl-4">
								{translate('Pages.specificCompanyJobPreference')}
							</Form.Label>
							<div key="radio" className="form-check-radio d-flex flex-wrap gap-2 gap-md-4">
								<Form.Check
									inline
									label={translate('Common.yes')}
									name="isCompanySizeSpecific"
									type="radio"
									value={'yes'}
									checked={formik.values.isCompanySizeSpecific === 'yes'}
									onChange={handleChange}
								/>
								<Form.Check
									inline
									label={translate('Common.no')}
									name="isCompanySizeSpecific"
									type="radio"
									value={'no'}
									checked={formik.values.isCompanySizeSpecific === 'no'}
									onChange={handleChange}
								/>
							</div>
							{renderError('isCompanySizeSpecific')}
						</Form.Group>
						{formik.values.isCompanySizeSpecific === 'yes' && (
							<Form.Group className="position-relative">
								<Form.Label className="mb-2 mb-md-3 mb-xxl-4">
									{translate('Pages.companySizeJobPreference')}
								</Form.Label>
								<div
									key="radio"
									className="form-check-radio d-flex flex-column flex-sm-row flex-wrap gap-2 gap-md-4"
								>
									{companyRangeList.length > 0 &&
										companyRangeList.map(
											(
												data: {
													maxEmployee: number;
													minEmployee: number;
												},
												index: number,
											) => {
												const range = [data?.minEmployee, data?.maxEmployee]
													.filter(Boolean)
													.join('-');
												return (
													<Form.Check
														inline
														key={index}
														label={`${range} ${translate('Common.employees')}`}
														name="companySize"
														type="radio"
														value={range}
														checked={values.companySize === range}
														onChange={handleChange}
													/>
												);
											},
										)}
								</div>
								{renderError('companySize')}
							</Form.Group>
						)}
					</Stack>
				</div>
				<div className="job-preference-box bg-white">
					<Stack className="job-preference-form">
						<div>
							<Form.Group className="position-relative search-select">
								<Form.Label className="mb-2 mb-md-3 mb-xxl-4">
									{translate('Pages.jobPreferenceRole')}
								</Form.Label>
								<CreatableSelect
									isSearchable
									name="jobRole"
									options={roles}
									getOptionValue={(option) => option.id ?? option.value}
									getOptionLabel={(option) => option.parentName ?? option.label}
									onChange={(optionValue: any) => {
										const valuesArr = [];
										if (formik.values.jobRole?.length) {
											valuesArr.push(...formik.values.jobRole, optionValue?.id?.toString());
										} else {
											valuesArr.push(optionValue?.id?.toString());
										}
										formik.setFieldValue('jobRole', valuesArr);
									}}
									onBlur={formik.handleBlur}
									autoFocus={false}
									className="form-react-select"
									classNamePrefix="select"
									placeholder={translate('PlaceHolder.addRole')}
									value={translate('PlaceHolder.addRole')}
									isOptionDisabled={(option: any) =>
										formik.values.jobRole.some((item: any) => Number(item) === option?.id)
									}
									onCreateOption={(value) => {
										const valuesArr = [];
										if (formik.values.newRole?.length) {
											valuesArr.push(...formik.values.newRole, value);
										} else {
											valuesArr.push(value);
										}
										formik.setFieldValue('newRole', [...new Set(valuesArr)]);
									}}
								/>
								{renderError('jobRole')}
								<i className="ri-search-line form-icon position-absolute lh-1"></i>
							</Form.Group>
							{(formik.values.jobRole?.length > 0 || formik.values.newRole?.length > 0) && (
								<div className="selected-items-list d-flex flex-wrap gap-2 gap-md-3 mt-3 mt-md-4">
									{[...formik.values.jobRole, ...formik.values.newRole].map(
										(roleId: any, index: number) => (
											<div
												key={index}
												className="selected-item-box d-flex align-items-center gap-2 rounded-pill"
											>
												<p className="mb-0">{getNameById(roles, roleId, 'parentName') ?? roleId}</p>
												<Button
													variant="link"
													className="p-0 text-decoration-none border-0"
													onClick={() => {
														const updatedRoles = formik.values.jobRole.filter(
															(roleValue: any) => roleValue !== roleId,
														);
														const updatedNewRoles = formik.values.newRole.filter(
															(roleValue: any) => roleValue !== roleId,
														);
														formik.setFieldValue('jobRole', updatedRoles);
														formik.setFieldValue('newRole', updatedNewRoles);
													}}
												>
													<i className="ri-close-line"></i>
												</Button>
											</div>
										),
									)}
								</div>
							)}
						</div>
					</Stack>
				</div>
				<div className="job-preference-box bg-white">
					<Stack className="job-preference-form">
						<Form.Group className="position-relative roles-radio">
							<Form.Label className="mb-2 mb-md-3 mb-xxl-4">
								{translate('Pages.jobPreferenceWouldLikeSee')}
							</Form.Label>
							<h6 className="mb-3 mb-md-4">{translate('Pages.jobPreferenceSelectRelevant')}</h6>
							<div
								key="radio"
								className="form-check-radio d-flex flex-column flex-md-row flex-wrap gap-2 gap-md-4 gap-xl-3"
							>
								{levels.length > 0 &&
									levels.map(
										(
											data: {
												id: number;
												title: string;
											},
											index: number,
										) => (
											<Form.Check
												inline
												key={index}
												label={data.title}
												type="checkbox"
												name="level"
												value={data.id}
												checked={formik.values.level.includes(data.id)}
												onChange={handleCheckboxChange}
												className="m-0"
											/>
										),
									)}
							</div>
							{renderError('level')}
						</Form.Group>
					</Stack>
				</div>
				<div className="job-preference-box bg-white">
					<Stack className="job-preference-form">
						<Form.Group className="position-relative">
							<Form.Label className="mb-2 mb-md-3 mb-xxl-4">
								{translate('Pages.jobPreferenceLanguageWork')}
							</Form.Label>
							<div className="select-area d-flex flex-wrap gap-2 gap-sm-3">
								{languages?.length > 0 &&
									languages.map(
										(
											data: {
												title: string;
												value: string;
											},
											index: number,
										) => {
											return (
												<Button
													key={index}
													className={`select-area-btn rounded-pill bg-transparent ${formik.values.language.includes(data.value) ? 'selected' : ''}`}
													value={data?.value}
													name="language"
													onClick={(evt) => {
														const target = evt.target as HTMLButtonElement;
														const updatedLanguageArray = formik.values.language.includes(
															target?.value,
														)
															? formik.values.language.filter((lang: any) => lang !== target?.value)
															: [...formik.values.language, target?.value];
														formik.setFieldValue('language', updatedLanguageArray);
													}}
												>
													{data?.title}
												</Button>
											);
										},
									)}
							</div>
							{renderError('language')}
						</Form.Group>
					</Stack>
				</div>
				<div className="job-preference-box bg-white">
					<Stack className="job-preference-form">
						<div>
							<Form.Group className="position-relative">
								<Form.Label className="mb-2 mb-md-3 mb-xxl-4">
									{translate('Pages.jobPreferenceSkillAdd')}
								</Form.Label>
								<ReactSelect
									isSearchable
									name="skills"
									options={skill}
									getOptionValue={(option) => option.id}
									getOptionLabel={(option) => option.title}
									onChange={(optionValue: any) => {
										const valuesArr = [];
										if (formik.values.skills?.length) {
											valuesArr.push(...formik.values.skills, optionValue);
										} else {
											valuesArr.push(optionValue);
										}
										formik.setFieldValue('skills', valuesArr);
									}}
									onBlur={formik.handleBlur}
									autoFocus={false}
									placeholder={translate('PlaceHolder.addSkill')}
									value={translate('PlaceHolder.addSkill')}
									className="form-react-select"
									classNamePrefix="select"
									isOptionDisabled={(option: any) =>
										formik.values.skills.some((item: any) => item?.id === option?.id)
									}
								/>
								{renderError('skills')}
							</Form.Group>
							{formik.values.skills?.length > 0 && (
								<div className="selected-items-list d-flex flex-wrap gap-2 gap-md-3 mt-3 mt-md-4">
									{formik.values.skills.map((skillData: any) => (
										<div
											key={skillData}
											className="selected-item-box d-flex align-items-center gap-2 rounded-pill"
										>
											<p className="mb-0">{skillData?.title}</p>
											<Button
												variant="link"
												className="p-0 text-decoration-none border-0"
												onClick={() => {
													const updatedSkills = formik.values.skills.filter(
														(skillValue: any) => skillValue !== skillData,
													);
													formik.setFieldValue('skills', updatedSkills);
												}}
											>
												<i className="ri-close-line"></i>
											</Button>
										</div>
									))}
								</div>
							)}
						</div>
					</Stack>
				</div>
				<div className="job-preference-box bg-white">
					<Stack className="job-preference-form">
						<Form.Group className="position-relative">
							<Form.Label className="mb-2 mb-md-3 mb-xxl-4">
								{translate('Pages.favouriteIndustries')}
							</Form.Label>
							<div key="radio" className="form-check-radio d-flex flex-wrap gap-2 gap-md-4">
								<Form.Check
									inline
									label={translate('Modal.yes')}
									name="isIndustoriesFavourite"
									type="radio"
									value={'yes'}
									checked={formik.values.isIndustoriesFavourite === 'yes'}
									onChange={handleChange}
								/>
								<Form.Check
									inline
									label={translate('Modal.no')}
									name="isIndustoriesFavourite"
									type="radio"
									value={'no'}
									checked={formik.values.isIndustoriesFavourite === 'no'}
									onChange={handleChange}
								/>
							</div>
							{renderError('isIndustoriesFavourite')}
						</Form.Group>
						{formik.values.isIndustoriesFavourite === 'yes' && (
							<div>
								<Form.Group className="position-relative">
									<Form.Label className="mb-2 mb-md-3 mb-xxl-4">
										{translate('Pages.industriesInterested')}
									</Form.Label>
									<ReactSelect
										isSearchable
										name="industries"
										options={industries}
										getOptionValue={(option) => option.id}
										getOptionLabel={(option) => option.title}
										onChange={(optionValue: any) => {
											const valuesArr = [];
											if (formik.values.industries?.length) {
												valuesArr.push(...formik.values.industries, optionValue?.id?.toString());
											} else {
												valuesArr.push(optionValue?.id?.toString());
											}
											formik.setFieldValue('industries', valuesArr);
										}}
										onBlur={formik.handleBlur}
										autoFocus={false}
										placeholder={translate('PlaceHolder.addIndustries')}
										value={translate('PlaceHolder.addIndustries')}
										className="form-react-select"
										classNamePrefix="select"
										isOptionDisabled={(option: any) =>
											formik.values.industries.some((item: any) => Number(item) === option?.id)
										}
									/>
									{renderError('industries')}
								</Form.Group>
								{formik.values.industries?.length > 0 && (
									<div className="selected-items-list d-flex flex-wrap gap-2 gap-md-3 mt-3 mt-md-4">
										{formik.values.industries.map((industry: any) => (
											<div
												key={industry}
												className="selected-item-box d-flex align-items-center gap-2 rounded-pill"
											>
												<p className="mb-0">{getNameById(industries, industry, 'title')}</p>
												<Button
													variant="link"
													className="p-0 text-decoration-none border-0"
													onClick={() => {
														const updatedIndustries = formik.values.industries.filter(
															(industryValue: any) => industryValue !== industry,
														);
														formik.setFieldValue('industries', updatedIndustries);
													}}
												>
													<i className="ri-close-line"></i>
												</Button>
											</div>
										))}
									</div>
								)}
							</div>
						)}
					</Stack>
				</div>
				<div className="job-preference-box bg-white">
					<Stack className="job-preference-form">
						<Form.Group className="position-relative">
							<Form.Label className="mb-2 mb-md-3 mb-xxl-4">
								{translate('Pages.minimumExpectedSalary')}
							</Form.Label>
							<div key="radio" className="form-check-radio d-flex flex-wrap gap-2 gap-md-4">
								<Form.Check
									inline
									label={translate('Common.annually')}
									name="salaryType"
									type="radio"
									value={'Annually'}
									checked={formik.values.salaryType === 'Annually'}
									onChange={(e: any) => {
										callSalaryRangeFunc(salaryArr, 'Annually');
										handleChange(e);
									}}
								/>
								<Form.Check
									inline
									label={translate('Common.monthly')}
									name="salaryType"
									type="radio"
									value={'Monthly'}
									checked={formik.values.salaryType === 'Monthly'}
									onChange={(e: any) => {
										callSalaryRangeFunc(salaryArr, 'Monthly');
										handleChange(e);
									}}
								/>
								<Form.Check
									inline
									label={translate('Common.hourly')}
									name="salaryType"
									type="radio"
									value={'Hourly'}
									checked={formik.values.salaryType === 'Hourly'}
									onChange={(e: any) => {
										callSalaryRangeFunc(salaryArr, 'Hourly');
										handleChange(e);
									}}
								/>
							</div>
							<div className="price-range-box mt-4">
								<h6 className="fw-bold">{translate('Pages.priceRange')}</h6>
								{/* <p>$40/month minimum</p> */}
								<div className="range-slider">
									<Range
										values={priceRange}
										step={STEP}
										min={min}
										max={max}
										onChange={(values) => {
											setPriceRange(values), formik.setFieldValue('salary', Number(values));
										}}
										renderTrack={({ props, children }) => (
											<div
												className="range-area d-flex w-100"
												onMouseDown={props.onMouseDown}
												onTouchStart={props.onTouchStart}
											>
												<div
													className="range-covered w-100"
													ref={props.ref}
													style={{
														background: getTrackBackground({
															values: priceRange,
															colors: ['#FF7F05', '#fff'],
															min: min,
															max: max,
														}),
													}}
												>
													{children}
												</div>
											</div>
										)}
										renderThumb={({ props }) => {
											// Remove 'key' from props before spreading
											const { key, ...restProps } = props;
											return (
												<div
													key={key}
													className="range-point d-flex align-items-center justify-content-center rounded-pill"
													{...restProps}
												>
													<div className="range-tag fw-bold bg-white position-absolute text-center">
														${unitNumber(priceRange[0], 1)}
													</div>
												</div>
											);
										}}
									/>
								</div>
							</div>
						</Form.Group>
						<Stack className="job-preference-box-info gap-2">
							<p className="mb-0 position-relative">{translate('Pages.priceRangeDescription1')}</p>
							<p className="mb-0 position-relative">{translate('Pages.priceRangeDescription2')}</p>
							<p className="mb-0 position-relative">{translate('Pages.priceRangeDescription3')} </p>
						</Stack>
						<Button className="filled-btn rounded-pill mt-2" type="submit" disabled={isPending}>
							{translate('Common.submit')}
						</Button>
					</Stack>
				</div>
			</Stack>
		</Form>
	);
};

export default JobPreference;
